import { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import { Outlet } from 'react-router';
import { toast, Toaster } from 'sonner';

import { AppRoutes } from '@/app/AppRoute';
import { ThemeProvider } from '@/app/ThemeContext';
import { E2EStatusMenu } from '@/components/E2EStatusMenu';
import { FeaturebaseWidget } from '@/components/FeaturebaseWidget';
import { LogRocketIdentifier } from '@/components/LogRocketIdentifier';
import StatsigWrapper from '@/components/statsig/StatsigWrapper';
import { useInitAxiosInterceptors } from '@/hooks/useAxios';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { queryClient } from '@/queryclient';
import AuthProvider from '@/state/auth';
import { GlobalStateProvider } from '@/state/global.state';
import { QueryClientProvider } from '@/utils/api';

function InitAxiosProvider({ children }: { children: ReactNode }) {
  useInitAxiosInterceptors();

  return children;
}

declare global {
  interface Window {
    toastDismiss: typeof toast.dismiss;
  }
}

// FeaturebaseErrorBoundary: Safety net for third-party Featurebase widget
// Prevents the entire app from crashing if the Featurebase SDK throws an error
export class FeaturebaseErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn(
      '[Featurebase] Widget error caught by boundary:',
      error,
      errorInfo
    );
  }

  render() {
    if (this.state.hasError) {
      // Widget is non-visual anyway, so just return null
      return null;
    }
    return this.props.children;
  }
}

// ThirdPartyProviders is a wrapper for third-party providers like react-query, react-router, etc.
function ThirdPartyProviders({ children }: { children: ReactNode }) {
  // Declare global type for window to allow toastDismiss property
  useEffect(() => {
    window.toastDismiss = toast.dismiss;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster richColors position="bottom-right" />
      {children}
    </QueryClientProvider>
  );
}

// AppProviders is a wrapper for providers like AuthProvider, etc that can be used anywhere in the app.
function AppProviders({ children }: { children: ReactNode }) {
  useVersionCheck({
    enableAutoRefresh: import.meta.env.PROD, // Auto-refresh in production only
    notifyUser: true,
    onVersionChange: (oldVersion, newVersion) => {
      // Development: Show toast notification
      if (import.meta.env.DEV) {
        toast.info(`App updated: ${oldVersion} → ${newVersion}`, {
          description: 'Storage cleared for new version',
          duration: 5000,
        });
      }
    },
  });

  return (
    <GlobalStateProvider>
      <AuthProvider>
        <ThemeProvider>
          <InitAxiosProvider>
            <LogRocketIdentifier />
            {children}
          </InitAxiosProvider>
        </ThemeProvider>
      </AuthProvider>
    </GlobalStateProvider>
  );
}

export function App() {
  return (
    <ThirdPartyProviders>
      <AppRoutes>
        <AppProviders>
          <StatsigWrapper>
            <Outlet />
            {/* E2E Status Menu - Development only */}
            {import.meta.env.DEV && <E2EStatusMenu />}
            {/* FeaturebaseWidget inside StatsigProvider to access gate values */}
            <FeaturebaseErrorBoundary>
              <FeaturebaseWidget />
            </FeaturebaseErrorBoundary>
          </StatsigWrapper>
        </AppProviders>
      </AppRoutes>
    </ThirdPartyProviders>
  );
}
